import {
  AppstoreOutlined,
  BankOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  FileTextOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Popconfirm,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { TableColumnsType, UploadFile } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { ApiError } from '../api/errors';
import {
  ShowcaseProjectCategory,
  ShowcaseProjectStatus,
  createShowcaseProject,
  deleteShowcaseProject,
  deleteShowcaseProjectDemo,
  deleteShowcaseProjectImage,
  listShowcaseProjects,
  setShowcaseProjectPublished,
  showcaseDemoExtensions,
  showcaseDemoMaxBytes,
  showcaseImageExtensions,
  showcaseImageMaxBytes,
  showcaseProjectCategoryNames,
  showcaseProjectStatusNames,
  updateShowcaseProject,
  uploadShowcaseProjectDemo,
  uploadShowcaseProjectImage,
} from '../api/showcaseProjects';
import type {
  SaveShowcaseProjectPayload,
  ShowcaseProject,
  ShowcaseProjectCategoryValue,
  ShowcaseProjectStatusValue,
} from '../api/showcaseProjects';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { formatDate } from './projects/projectDisplay';
import { ShowcaseProjectCard } from './showcase/ShowcaseProjectCard';
import { ShowcaseSummary } from './showcase/ShowcaseSummary';
import {
  ShowcaseImage,
  categoryTag,
  deleteConfirmText,
  statusColors,
  statusTag,
  technologyTags,
} from './showcase/showcaseDisplay';
import { BrandColors } from '../theme/brand';

/** Giá trị của biểu mẫu khai và sửa. Ảnh và demo không nằm ở đây: chúng là tệp, giữ trong trạng thái riêng. */
interface FormValues {
  readonly title: string;
  readonly clientName: string;
  readonly category: ShowcaseProjectCategoryValue;
  readonly status: ShowcaseProjectStatusValue;
  readonly startDate: Dayjs;
  readonly endDate: Dayjs | null | undefined;
  readonly progressPercent: number;
  readonly description: string;
  readonly technologies: readonly string[] | undefined;
  /** Mỗi dòng một thành tích. */
  readonly achievementsText: string | undefined;
  readonly isPublished: boolean;
  readonly sortOrder: number;
  /** Đường dẫn của link gửi khách. Để trống thì máy chủ tự tạo từ tên dự án. */
  readonly demoSlug: string | undefined;
}

/** Bộ lọc cờ hiện trên website. */
type VisibilityFilter = 'all' | 'published' | 'hidden';

/** Cách bày danh sách: bảng để quản trị nhanh, thẻ để xem trước như trên website. */
type ViewMode = 'table' | 'cards';

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly ShowcaseProject[];
  readonly totalCount: number;
}

/** Một việc với tệp cần làm sau khi lưu thông tin dự án. */
interface FileStep {
  readonly label: string;
  readonly run: () => Promise<unknown>;
}

/**
 * Số bản ghi mỗi trang.
 *
 * Lớn hơn mặc định của hệ thống: danh sách này là toàn bộ trang dự án tiêu biểu của website — vài
 * chục dự án — và người quản trị cần thấy trọn thứ tự hiển thị trên một màn hình.
 */
const pageSize = 50;

const categoryOptions = Object.entries(showcaseProjectCategoryNames).map(([value, label]) => ({
  value: Number(value) as ShowcaseProjectCategoryValue,
  label,
}));

const statusOptions = Object.entries(showcaseProjectStatusNames).map(([value, label]) => ({
  value: Number(value) as ShowcaseProjectStatusValue,
  label,
}));

const visibilityOptions: { value: VisibilityFilter; label: string }[] = [
  { value: 'all', label: 'Tất cả' },
  { value: 'published', label: 'Đang hiện' },
  { value: 'hidden', label: 'Đang ẩn' },
];

const viewModeOptions: { value: ViewMode; label: string; icon: ReactNode }[] = [
  { value: 'table', label: 'Bảng', icon: <UnorderedListOutlined /> },
  { value: 'cards', label: 'Thẻ', icon: <AppstoreOutlined /> },
];

/** Ô kéo thả tệp: cùng tỉ lệ 16:9 với ảnh, để hai ô ảnh và demo cao bằng nhau. */
const dropZoneStyle: CSSProperties = {
  aspectRatio: '16 / 9',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
};

/** Dòng ngay dưới ô kéo thả: tên tệp đang chờ hoặc link mở, và nút gỡ. */
const fileFooterStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  minHeight: 24,
  marginTop: 6,
};

/** Tiêu đề một nhóm trong biểu mẫu, kèm đường kẻ mảnh kéo hết bề ngang. */
function SectionTitle({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 12px' }}>
      <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
        {children}
      </Typography.Text>
      <div style={{ flex: 1, height: 1, background: BrandColors.border }} />
    </div>
  );
}

/** Đổi chữ nhiều dòng thành danh sách, bỏ dòng trống. */
function linesToList(text: string | undefined): string[] {
  return (text ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/** Dung lượng tệp dễ đọc. */
function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} MB`;
  }

  return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('vi-VN')} KB`;
}

/**
 * Kiểm một tệp ngay khi người dùng chọn, trước khi đưa vào biểu mẫu.
 *
 * Máy chủ vẫn kiểm lại; kiểm ở đây chỉ để người dùng biết ngay, không phải đợi tới lúc bấm Lưu.
 */
function isAcceptableFile(
  file: File,
  extensions: readonly string[],
  maxBytes: number,
  kind: string,
): boolean {
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

  if (!extensions.includes(extension)) {
    message.error(`${kind} chỉ nhận tệp ${extensions.join(', ')}.`);

    return false;
  }

  if (file.size === 0) {
    message.error('Tệp rỗng nên không dùng được.');

    return false;
  }

  if (file.size > maxBytes) {
    message.error(`${kind} tối đa ${formatSize(maxBytes)}, tệp này ${formatSize(file.size)}.`);

    return false;
  }

  return true;
}

/**
 * Diễn giải lỗi khi xử lý tệp.
 *
 * Máy chủ chưa có điểm cuối nhận tệp thì trả 404 hoặc 405 không kèm mã lỗi nghiệp vụ — trường hợp đó
 * nói rõ là chức năng chưa bật, thay vì để người dùng đọc một chữ "Not Found" không giải thích gì.
 */
function describeFileError(error: unknown): string {
  if (error instanceof ApiError) {
    if ((error.status === 404 || error.status === 405) && error.errorCode === undefined) {
      return 'máy chủ chưa bật chức năng lưu tệp (đang chờ cấu hình Cloudflare R2)';
    }

    return error.message;
  }

  return 'lỗi không xác định';
}

/**
 * Trang "Demo dự án" — quản lý nội dung trang Dự án tiêu biểu trên website công ty.
 *
 * Mỗi dòng là một thẻ trên website: các cột bám đúng những gì thẻ hiển thị, cộng thêm link demo, cờ
 * hiện trên web và thứ tự hiển thị. Danh sách sắp đúng thứ tự website sẽ dùng, nên nhìn bảng là biết
 * trang công khai trông thế nào.
 *
 * <b>Ảnh và demo là tệp, không phải đường dẫn gõ tay.</b> Tệp được chọn trong biểu mẫu và chỉ gửi đi
 * khi bấm Lưu, SAU khi thông tin dự án đã lưu xong — dự án mới phải có định danh thì tệp mới có chỗ
 * gắn vào. Tải tệp hỏng thì phần thông tin vẫn được giữ, và người dùng được báo đúng phần tệp nào chưa
 * xong để tải lại.
 *
 * <b>Ẩn trước khi xóa.</b> Muốn tạm gỡ một dự án khỏi website thì tắt công tắc "Hiện trên web" — nội
 * dung còn nguyên để bật lại.
 */
export function ShowcaseProjectPage(): ReactNode {
  const { hasPermission } = useAuth();

  const canManage = hasPermission(PermissionCodes.ShowcaseProjectManage);

  const [form] = Form.useForm<FormValues>();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState<ShowcaseProjectCategoryValue | null>(null);
  const [status, setStatus] = useState<ShowcaseProjectStatusValue | null>(null);
  const [visibility, setVisibility] = useState<VisibilityFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editing, setEditing] = useState<ShowcaseProject | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Tệp đang chọn trong biểu mẫu — chưa gửi đi cho tới lúc bấm Lưu.
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const [demoFile, setDemoFile] = useState<File | null>(null);
  const [isDemoRemoved, setIsDemoRemoved] = useState(false);

  // Ảnh xem trước là một địa chỉ tạm trong bộ nhớ trình duyệt; phải trả lại khi không dùng nữa.
  useEffect(() => {
    return () => {
      if (imagePreview !== null) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const requestKey = useMemo(
    () => JSON.stringify({ searchTerm, category, status, visibility, pageNumber, reloadToken }),
    [searchTerm, category, status, visibility, pageNumber, reloadToken],
  );

  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        const page = await listShowcaseProjects({
          searchTerm,
          pageNumber,
          pageSize,
          ...(category === null ? {} : { category }),
          ...(status === null ? {} : { status }),
          ...(visibility === 'all' ? {} : { isPublished: visibility === 'published' }),
        });

        if (!isCurrent) {
          return;
        }

        setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh sách dự án tiêu biểu.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [requestKey, searchTerm, category, status, visibility, pageNumber]);

  const reload = useCallback(() => { setReloadToken((token) => token + 1); }, []);

  /**
   * Gợi ý công nghệ cho ô nhập: mọi công nghệ đã dùng ở các dự án đang tải, để cùng một công nghệ
   * không bị gõ thành hai cách viết khác nhau trên website.
   */
  const technologyOptions = useMemo(() => {
    const names = new Set<string>();

    for (const project of loaded?.items ?? []) {
      for (const technology of project.technologies) {
        names.add(technology);
      }
    }

    return [...names]
      .sort((left, right) => left.localeCompare(right, 'vi'))
      .map((name) => ({ value: name, label: name }));
  }, [loaded]);

  /** Ảnh đang hiện trong biểu mẫu: ảnh vừa chọn, hoặc ảnh hiện có nếu chưa bị gỡ. */
  const shownImageUrl = imagePreview ?? (isImageRemoved ? null : (editing?.imageUrl ?? null));

  /** Demo hiện có, nếu chưa bị gỡ — ưu tiên link gửi khách trên tên miền công ty. */
  const existingDemoUrl = isDemoRemoved ? null : (editing?.demoShareUrl ?? editing?.demoUrl ?? null);

  function resetFiles(): void {
    setImageFile(null);
    setImagePreview(null);
    setIsImageRemoved(false);
    setDemoFile(null);
    setIsDemoRemoved(false);
  }

  function openCreate(): void {
    setEditing(null);
    resetFiles();
    form.resetFields();
    form.setFieldsValue({
      category: ShowcaseProjectCategory.ComputerVision,
      status: ShowcaseProjectStatus.Ongoing,
      progressPercent: 0,
      isPublished: true,
      sortOrder: 0,
      technologies: [],
    });
    setIsFormOpen(true);
  }

  function openEdit(project: ShowcaseProject): void {
    setEditing(project);
    resetFiles();
    form.resetFields();
    form.setFieldsValue({
      title: project.title,
      clientName: project.clientName,
      category: project.category,
      status: project.status,
      startDate: dayjs(project.startDate.slice(0, 10)),
      endDate: project.endDate === null ? null : dayjs(project.endDate.slice(0, 10)),
      progressPercent: project.progressPercent,
      description: project.description,
      technologies: [...project.technologies],
      achievementsText: project.achievements.join('\n'),
      isPublished: project.isPublished,
      sortOrder: project.sortOrder,
      demoSlug: project.demoSlug ?? undefined,
    });
    setIsFormOpen(true);
  }

  function closeForm(): void {
    setIsFormOpen(false);
    resetFiles();
  }

  function pickImage(file: File): boolean {
    if (isAcceptableFile(file, showcaseImageExtensions, showcaseImageMaxBytes, 'Ảnh')) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setIsImageRemoved(false);
    }

    // Luôn chặn phép tải lên mặc định: tệp chỉ được gửi khi bấm Lưu, kèm phiếu xác thực.
    return false;
  }

  function removeImage(): void {
    setImageFile(null);
    setImagePreview(null);
    setIsImageRemoved(true);
  }

  function pickDemo(file: File): boolean {
    if (isAcceptableFile(file, showcaseDemoExtensions, showcaseDemoMaxBytes, 'Trang demo')) {
      setDemoFile(file);
      setIsDemoRemoved(false);
    }

    return false;
  }

  function removeDemo(): void {
    setDemoFile(null);
    setIsDemoRemoved(true);
  }

  /** Những việc với tệp cần làm sau khi thông tin dự án đã lưu. */
  function fileSteps(id: string, hadImage: boolean, hadDemo: boolean): FileStep[] {
    const steps: FileStep[] = [];

    if (imageFile !== null) {
      steps.push({ label: 'tải ảnh', run: () => uploadShowcaseProjectImage(id, imageFile) });
    } else if (isImageRemoved && hadImage) {
      steps.push({ label: 'gỡ ảnh', run: () => deleteShowcaseProjectImage(id) });
    }

    if (demoFile !== null) {
      steps.push({ label: 'tải trang demo', run: () => uploadShowcaseProjectDemo(id, demoFile) });
    } else if (isDemoRemoved && hadDemo) {
      steps.push({ label: 'gỡ trang demo', run: () => deleteShowcaseProjectDemo(id) });
    }

    return steps;
  }

  async function submit(values: FormValues): Promise<void> {
    const payload: SaveShowcaseProjectPayload = {
      title: values.title,
      clientName: values.clientName,
      category: values.category,
      status: values.status,
      startDate: values.startDate.format('YYYY-MM-DD'),
      endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
      progressPercent: values.progressPercent,
      description: values.description,
      technologies: values.technologies ?? [],
      achievements: linesToList(values.achievementsText),
      // Đường dẫn tệp không sửa ở đây: giữ nguyên giá trị hiện có, việc thay hoặc gỡ tệp đi qua
      // các lời gọi riêng ở dưới để máy chủ dọn luôn tệp cũ trong kho.
      imageUrl: editing?.imageUrl ?? null,
      demoUrl: editing?.demoUrl ?? null,
      isPublished: values.isPublished,
      sortOrder: values.sortOrder,
      demoSlug: values.demoSlug === undefined || values.demoSlug.trim() === '' ? null : values.demoSlug.trim(),
    };

    setIsSaving(true);

    let saved: ShowcaseProject;

    try {
      saved = editing === null
        ? await createShowcaseProject(payload)
        : await updateShowcaseProject(editing.id, payload);
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được.');
      setIsSaving(false);

      return;
    }

    const problems: string[] = [];

    const hadImage = (editing?.imageUrl ?? null) !== null;
    const hadDemo = (editing?.demoUrl ?? null) !== null;

    for (const step of fileSteps(saved.id, hadImage, hadDemo)) {
      try {
        await step.run();
      } catch (error) {
        problems.push(`${step.label}: ${describeFileError(error)}`);
      }
    }

    if (problems.length === 0) {
      message.success(editing === null ? 'Đã thêm dự án tiêu biểu.' : 'Đã lưu thay đổi.');
    } else {
      message.warning(
        `Đã lưu thông tin dự án, nhưng chưa xong phần tệp — ${problems.join('; ')}.`,
        8,
      );
    }

    setIsSaving(false);
    setIsFormOpen(false);
    resetFiles();
    form.resetFields();
    reload();
  }

  async function togglePublished(project: ShowcaseProject, isPublished: boolean): Promise<void> {
    try {
      await setShowcaseProjectPublished(project.id, isPublished);
      message.success(isPublished ? 'Đã hiện lên website.' : 'Đã ẩn khỏi website.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được.');
    }
  }

  async function remove(project: ShowcaseProject): Promise<void> {
    try {
      await deleteShowcaseProject(project.id);
      message.success('Đã xóa dự án tiêu biểu.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xóa được.');
    }
  }

  const columns: TableColumnsType<ShowcaseProject> = [
    {
      title: '#',
      dataIndex: 'sortOrder',
      width: 56,
      align: 'center',
      render: (sortOrder: number) => (
        <Tooltip title="Thứ tự hiển thị trên website — số nhỏ đứng trước">
          <Typography.Text type="secondary">{sortOrder}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: 'Dự án',
      key: 'project',
      render: (_, project) => (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <ShowcaseImage
            url={project.imageUrl}
            alt={project.title}
            isDimmed={!project.isPublished}
            style={{ width: 112 }}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <Space size={4} style={{ marginBottom: 4 }}>
              {categoryTag(project.category)}
              {project.isPublished ? null : <Tag style={{ marginInlineEnd: 0 }}>Đang ẩn</Tag>}
            </Space>
            <Typography.Paragraph
              strong
              style={{ marginBottom: 2 }}
              ellipsis={{ rows: 2, tooltip: project.title }}
            >
              {project.title}
            </Typography.Paragraph>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12 }}
              ellipsis={{ tooltip: project.clientName }}
            >
              <BankOutlined /> {project.clientName}
            </Typography.Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Tình trạng',
      dataIndex: 'status',
      width: 150,
      render: (status: ShowcaseProjectStatusValue, project) => (
        <div>
          {statusTag(status)}
          <Progress
            percent={project.progressPercent}
            size="small"
            strokeColor={statusColors[status]}
            format={(percent) => `${(percent ?? 0).toString()}%`}
            style={{ margin: '6px 0 0' }}
          />
        </div>
      ),
    },
    {
      title: 'Thời gian',
      key: 'period',
      width: 120,
      render: (_, project) => (
        <div>
          <div>{formatDate(project.startDate)}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {project.endDate === null ? 'đến nay' : `đến ${formatDate(project.endDate)}`}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Công nghệ',
      dataIndex: 'technologies',
      width: 250,
      render: (technologies: readonly string[]) => technologyTags(technologies),
    },
    {
      title: 'Demo',
      dataIndex: 'demoUrl',
      width: 110,
      align: 'center',
      render: (demoUrl: string | null, project: ShowcaseProject) => {
        // Link gửi khách trên tên miền công ty; dự án chưa có thì dùng tạm địa chỉ trong kho.
        const link = project.demoShareUrl ?? demoUrl;

        return link === null ? (
          <Typography.Text type="secondary">—</Typography.Text>
        ) : (
          <Space size={0}>
            <Button
              type="link"
              size="small"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              icon={<ExportOutlined />}
              iconPosition="end"
            >
              Xem
            </Button>
            <Typography.Text copyable={{ text: link, tooltips: ['Sao chép link gửi khách', 'Đã sao chép'] }} />
          </Space>
        );
      },
    },
    {
      title: 'Hiện trên web',
      dataIndex: 'isPublished',
      width: 130,
      align: 'center',
      render: (isPublished: boolean, project) => (
        <Switch
          size="small"
          checked={isPublished}
          disabled={!canManage}
          onChange={(checked) => void togglePublished(project, checked)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 92,
      align: 'center',
      fixed: 'right',
      render: (_, project) => (
        <Space size={2}>
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Sửa"
              disabled={!canManage}
              onClick={() => { openEdit(project); }}
            />
          </Tooltip>
          <Popconfirm
            title={deleteConfirmText.title}
            description={deleteConfirmText.description}
            okText="Xóa"
            cancelText="Không"
            okButtonProps={{ danger: true }}
            disabled={!canManage}
            onConfirm={() => { void remove(project); }}
          >
            <Tooltip title="Xóa">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label="Xóa"
                disabled={!canManage}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const items = loaded?.items ?? [];

  const hasDemo = demoFile !== null || existingDemoUrl !== null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Demo dự án
          </Typography.Title>
          <Typography.Text type="secondary">
            Nội dung trang{' '}
            <a href="https://mvalab.vn/du-an-tieu-bieu" target="_blank" rel="noopener noreferrer">
              Dự án tiêu biểu <ExportOutlined />
            </a>{' '}
            trên website công ty.
          </Typography.Text>
        </div>
        <Space wrap>
          <Segmented<ViewMode> options={viewModeOptions} value={viewMode} onChange={setViewMode} />
          <Button type="primary" icon={<PlusOutlined />} disabled={!canManage} onClick={openCreate}>
            Thêm dự án
          </Button>
        </Space>
      </div>

      <ShowcaseSummary reloadToken={reloadToken} />

      <Card size="small" styles={{ body: { padding: 12 } }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input.Search
            allowClear
            placeholder="Tìm theo tên dự án hoặc khách hàng"
            style={{ flex: '1 1 220px', maxWidth: 360 }}
            onSearch={(value) => {
              setSearchTerm(value);
              setPageNumber(1);
            }}
          />
          <Select<ShowcaseProjectCategoryValue>
            allowClear
            placeholder="Lĩnh vực"
            style={{ width: 160 }}
            options={categoryOptions}
            onChange={(value: ShowcaseProjectCategoryValue | undefined) => {
              // Bấm nút xóa lựa chọn thì thư viện gọi lại với undefined, không phải giá trị hợp lệ.
              setCategory(value ?? null);
              setPageNumber(1);
            }}
          />
          <Select<ShowcaseProjectStatusValue>
            allowClear
            placeholder="Tình trạng"
            style={{ width: 150 }}
            options={statusOptions}
            onChange={(value: ShowcaseProjectStatusValue | undefined) => {
              setStatus(value ?? null);
              setPageNumber(1);
            }}
          />
          <Segmented<VisibilityFilter>
            options={visibilityOptions}
            value={visibility}
            onChange={(value) => {
              setVisibility(value);
              setPageNumber(1);
            }}
          />
          <Tooltip title="Tải lại">
            <Button
              icon={<ReloadOutlined />}
              aria-label="Tải lại"
              onClick={reload}
              style={{ marginInlineStart: 'auto' }}
            />
          </Tooltip>
        </div>
      </Card>

      {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

      {viewMode === 'table' ? (
        <Table<ShowcaseProject>
          rowKey="id"
          columns={columns}
          dataSource={loaded?.items}
          loading={isLoading}
          tableLayout="fixed"
          scroll={{ x: 1200 }}
          pagination={{
            current: pageNumber,
            pageSize,
            total: loaded?.totalCount ?? 0,
            showSizeChanger: false,
            onChange: setPageNumber,
            showTotal: (total) => `${total.toString()} dự án`,
          }}
        />
      ) : (
        <Spin spinning={isLoading}>
          {loaded !== null && items.length === 0 ? (
            <Card>
              <Empty description="Không có dự án nào khớp bộ lọc." />
            </Card>
          ) : (
            <Row gutter={[16, 16]} style={{ minHeight: 200 }}>
              {items.map((project) => (
                <Col key={project.id} xs={24} md={12} xl={8} xxl={6}>
                  <ShowcaseProjectCard
                    project={project}
                    canManage={canManage}
                    onEdit={openEdit}
                    onDelete={(target) => { void remove(target); }}
                    onTogglePublished={(target, isPublished) => {
                      void togglePublished(target, isPublished);
                    }}
                  />
                </Col>
              ))}
            </Row>
          )}
          <Pagination
            align="end"
            hideOnSinglePage
            current={pageNumber}
            pageSize={pageSize}
            total={loaded?.totalCount ?? 0}
            showSizeChanger={false}
            onChange={setPageNumber}
            showTotal={(total) => `${total.toString()} dự án`}
            style={{ marginTop: 16 }}
          />
        </Spin>
      )}

      <Modal
        open={isFormOpen}
        width={880}
        title={editing === null ? 'Thêm dự án tiêu biểu' : `Sửa: ${editing.title}`}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={closeForm}
        onOk={() => { form.submit(); }}
        // Thân hộp thoại tự cuộn, để nút Lưu luôn nằm trong tầm mắt dù biểu mẫu dài.
        styles={{
          body: {
            maxHeight: 'calc(100vh - 240px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingInlineEnd: 4,
          },
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => void submit(values)}
          onValuesChange={(changed: Partial<FormValues>) => {
            // Hoàn thành thì tiến độ phải là 100%: điền sẵn để người nhập không phải nhớ.
            if (changed.status === ShowcaseProjectStatus.Completed) {
              form.setFieldValue('progressPercent', 100);
            }
          }}
        >
          <SectionTitle>Thông tin chung</SectionTitle>

          <Row gutter={16}>
            <Col span={14}>
              <Form.Item
                name="title"
                label="Tên dự án"
                rules={[{ required: true, message: 'Chưa nhập tên dự án.' }]}
              >
                <Input placeholder="Hệ Thống Điều Khiển Robot Công Nghiệp" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                name="clientName"
                label="Khách hàng"
                rules={[{ required: true, message: 'Chưa nhập khách hàng.' }]}
              >
                <Input placeholder="Công ty TNHH ..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="category"
                label="Lĩnh vực"
                rules={[{ required: true, message: 'Chưa chọn lĩnh vực.' }]}
              >
                <Select options={categoryOptions} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="status"
                label="Tình trạng"
                rules={[{ required: true, message: 'Chưa chọn tình trạng.' }]}
              >
                <Select options={statusOptions} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="progressPercent"
                label="Tiến độ (%)"
                rules={[{ required: true, message: 'Chưa nhập tiến độ.' }]}
              >
                <InputNumber<number> min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="startDate"
                label="Bắt đầu"
                rules={[{ required: true, message: 'Chưa chọn ngày.' }]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="endDate" label="Kết thúc" tooltip="Bắt buộc khi đã hoàn thành.">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <SectionTitle>Nội dung trên thẻ</SectionTitle>

          <Form.Item
            name="description"
            label="Mô tả ngắn"
            rules={[{ required: true, message: 'Chưa nhập mô tả.' }]}
          >
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} showCount maxLength={2000} />
          </Form.Item>

          <Form.Item
            name="technologies"
            label="Công nghệ"
            extra="Gõ rồi nhấn Enter. Thứ tự là thứ tự hiển thị — thẻ trên website chỉ hiện 4 công nghệ đầu."
          >
            <Select mode="tags" tokenSeparators={[',']} options={technologyOptions} />
          </Form.Item>

          <Form.Item
            name="achievementsText"
            label="Thành tích"
            extra="Mỗi dòng một thành tích. Thẻ trên website hiện 2 dòng đầu, cửa sổ chi tiết hiện hết."
          >
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 8 }}
              placeholder={'Điều khiển 8 robot hàn đồng thời\nGiảm 50% thời gian setup'}
            />
          </Form.Item>

          <SectionTitle>Ảnh và trang demo</SectionTitle>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Ảnh minh họa"
                extra={`JPG, PNG hoặc WEBP, tối đa ${formatSize(showcaseImageMaxBytes)}. Để trống thì website dùng ảnh mặc định.`}
              >
                <Upload.Dragger
                  accept={showcaseImageExtensions.join(',')}
                  showUploadList={false}
                  fileList={[] as UploadFile[]}
                  beforeUpload={pickImage}
                >
                  <div style={{ padding: '0 16px' }}>
                    {shownImageUrl === null ? (
                      <div style={dropZoneStyle}>
                        <PictureOutlined style={{ fontSize: 32, color: BrandColors.blue }} />
                        <Typography.Text>Kéo ảnh vào đây hoặc bấm để chọn</Typography.Text>
                      </div>
                    ) : (
                      <img
                        src={shownImageUrl}
                        alt="Ảnh minh họa"
                        style={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          objectFit: 'cover',
                          borderRadius: 6,
                          display: 'block',
                        }}
                      />
                    )}
                  </div>
                </Upload.Dragger>
                <div style={fileFooterStyle}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                    {imageFile !== null
                      ? `${imageFile.name} · ${formatSize(imageFile.size)} — tải lên khi bấm Lưu`
                      : shownImageUrl !== null
                        ? 'Bấm vào ảnh hoặc kéo ảnh khác vào để đổi.'
                        : ''}
                  </Typography.Text>
                  {shownImageUrl !== null ? (
                    <Button
                      danger
                      type="link"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={removeImage}
                    >
                      Gỡ ảnh
                    </Button>
                  ) : null}
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Trang demo"
                extra={`Một tệp .html tự chứa (ảnh, CSS, JS nhúng sẵn bên trong), tối đa ${formatSize(showcaseDemoMaxBytes)}.`}
              >
                <Upload.Dragger
                  accept={showcaseDemoExtensions.join(',')}
                  showUploadList={false}
                  fileList={[] as UploadFile[]}
                  beforeUpload={pickDemo}
                >
                  <div style={{ ...dropZoneStyle, padding: '0 16px' }}>
                    <FileTextOutlined style={{ fontSize: 32, color: hasDemo ? BrandColors.green : BrandColors.blue }} />
                    {demoFile !== null ? (
                      <>
                        <Typography.Text strong ellipsis style={{ maxWidth: '100%' }}>
                          {demoFile.name}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {formatSize(demoFile.size)} — tải lên khi bấm Lưu
                        </Typography.Text>
                      </>
                    ) : existingDemoUrl !== null ? (
                      <>
                        <Typography.Text strong>Đã có trang demo</Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Kéo tệp khác vào đây hoặc bấm để thay
                        </Typography.Text>
                      </>
                    ) : (
                      <Typography.Text>Kéo tệp .html vào đây hoặc bấm để chọn</Typography.Text>
                    )}
                  </div>
                </Upload.Dragger>
                <div style={fileFooterStyle}>
                  {existingDemoUrl !== null && demoFile === null ? (
                    <a
                      href={existingDemoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 12 }}
                    >
                      <ExportOutlined /> Mở demo hiện tại
                    </a>
                  ) : (
                    <span />
                  )}
                  {hasDemo ? (
                    <Button
                      danger
                      type="link"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={removeDemo}
                    >
                      Gỡ demo
                    </Button>
                  ) : null}
                </div>
              </Form.Item>
            </Col>
          </Row>

          {hasDemo ? (
            <Form.Item
              name="demoSlug"
              label="Đường dẫn của link gửi khách"
              extra={
                editing?.demoShareUrl ? (
                  <>
                    Link gửi khách:{' '}
                    <Typography.Text copyable={{ tooltips: ['Sao chép', 'Đã sao chép'] }}>
                      {editing.demoShareUrl}
                    </Typography.Text>
                  </>
                ) : (
                  'Để trống thì tự tạo từ tên dự án khi lưu. Chỉ giữ chữ thường không dấu, chữ số và gạch nối.'
                )
              }
            >
              <Input placeholder="vi-du: he-thong-kiem-tra-pcb" maxLength={120} />
            </Form.Item>
          ) : null}

          <SectionTitle>Hiển thị trên website</SectionTitle>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="sortOrder" label="Thứ tự hiển thị" extra="Số nhỏ đứng trước.">
                <InputNumber<number> min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isPublished" label="Hiện trên website" valuePropName="checked">
                <Switch checkedChildren="Hiện" unCheckedChildren="Ẩn" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Space>
  );
}
